// Vollea — League Detail with Standings + Fixtures tabs (and organiser controls)
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoChevronBack, IoCopyOutline, IoCalendarOutline, IoFlash, IoArrowForward, IoSettingsOutline, IoShareSocialOutline, IoConstructOutline, IoCreateOutline } from "react-icons/io5";
import { Card, Pill, Avatar } from "../components/ui-vollea";
import FixtureGeneratorModal from "../components/FixtureGeneratorModal";
import LeagueManageModal from "../components/LeagueManageModal";
import OrganiserToolsModal from "../components/OrganiserToolsModal";
import OrganiserScoreModal from "../components/OrganiserScoreModal";
import TeamPanel from "../components/TeamPanel";
import ShareCardModal from "../components/ShareCardModal";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { C, SP, R } from "../lib/theme";

const dateLabel = (iso) => {
  if (!iso) return "Date TBC";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "Date TBC";
  return d.toLocaleString(undefined, { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};
const isOverdue = (m) => { if (!m.scheduled_at || m.status !== "scheduled") return false; const d = new Date(m.scheduled_at); return !isNaN(d.getTime()) && d.getTime() < Date.now(); };

export default function LeagueDetail() {
  const { lid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [league, setLeague] = useState(null);
  const [table, setTable] = useState({ rows: [] });
  const [division, setDivision] = useState("");
  const [tab, setTab] = useState("standings");
  const [matches, setMatches] = useState([]);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [showGen, setShowGen] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [toolsInitialTab, setToolsInitialTab] = useState("members");
  const [scoreMatch, setScoreMatch] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [standingsMode, setStandingsMode] = useState("players"); // "players" | "teams"
  const [teamTable, setTeamTable] = useState([]);

  // Backend authoritatively enforces ownership in can_manage_league(). The
  // UI gate is intentionally permissive so the Manage button is shown to any
  // club_admin; users without permission will see a 403 from the backend if
  // they try to act. (Frontend doesn't have the club_id for the user object.)
  const canManage = user && (
    user.role === "super_admin" ||
    user.role === "club_admin" ||
    (user.role === "league_organiser" && league && league.organiser_id === user.user_id)
  );

  const loadLeague = useCallback(async () => {
    try {
      const data = await api.get(`/leagues/${lid}`);
      setLeague(data);
      const firstDiv = data.divisions?.[0] || "";
      if (!division) setDivision(firstDiv);
    } catch (e) { setErr(e.message); }
  }, [lid, division]);

  const loadMatches = useCallback(async () => {
    try { setMatches(await api.get(`/matches?league_id=${lid}`) || []); }
    catch { setMatches([]); }
  }, [lid]);

  useEffect(() => { loadLeague(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [lid]);

  const loadTable = useCallback(async () => {
    if (!division) return;
    try {
      const d = await api.get(`/leagues/${lid}/table?division=${encodeURIComponent(division)}`);
      setTable(d || []);
    } catch { setTable([]); }
  }, [lid, division]);

  const loadTeamTable = useCallback(async () => {
    if (!division) return;
    try {
      const d = await api.get(`/leagues/${lid}/teams/table?division=${encodeURIComponent(division)}`);
      setTeamTable(Array.isArray(d) ? d : []);
    } catch { setTeamTable([]); }
  }, [lid, division]);

  useEffect(() => { if (league) { loadTable(); loadTeamTable(); } }, [league, loadTable, loadTeamTable]);

  useEffect(() => { if (tab === "fixtures") loadMatches(); }, [tab, loadMatches]);
  useEffect(() => { if (tab === "standings") { loadTable(); loadTeamTable(); } }, [tab, loadTable, loadTeamTable]);

  const copyCode = () => {
    if (!league?.code) return;
    navigator.clipboard?.writeText(league.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Standings can come back as a bare array or {rows:[...]} depending on the
  // endpoint; normalise here so winner-card logic always sees an array.
  const standingsRows = Array.isArray(table) ? table : (table?.rows || []);
  const winner = standingsRows[0] || null;
  const runners = standingsRows.slice(1, 3);

  if (err) return (
    <div style={{ padding: SP.lg, color: C.error }}>
      <button onClick={() => navigate(-1)} style={{ color: C.text, marginBottom: SP.md }}><IoChevronBack size={26} /></button>
      {err}
    </div>
  );

  if (!league) return <div style={{ padding: SP.xl, textAlign: "center", color: C.lime }}>Loading…</div>;

  const filteredMatches = matches.filter((m) => !division || m.division === division || !m.division);
  const isArchived = !!league.archived;

  return (
    <div style={{ background: C.bg, minHeight: "100vh", paddingBottom: 90 }}>
      <div style={{ padding: SP.lg }}>
        <button onClick={() => navigate(-1)} data-testid="league-detail-back" style={{ color: C.text, marginBottom: SP.md }}>
          <IoChevronBack size={26} />
        </button>
        <h1 data-testid="league-detail-title" style={{ color: C.text, fontWeight: 700, fontSize: 28, margin: 0, letterSpacing: 0.5 }}>{league.name}</h1>
        {league.description && <p style={{ color: C.textMuted, fontSize: 14, margin: "6px 0 0" }}>{league.description}</p>}
        {isArchived && (
          <div data-testid="league-archived-banner" style={{ marginTop: SP.md, background: "rgba(245,158,11,0.1)", border: `1px solid ${C.warning}`, padding: SP.md, borderRadius: R.md, color: C.warning, fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
            ⏸ This league is archived — locked from new joins.
          </div>
        )}
      </div>

      <div style={{ padding: `0 ${SP.lg}px` }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>LEAGUE CODE</div>
              <div data-testid="league-code" style={{ color: C.text, fontWeight: 700, fontSize: 22, fontFamily: "monospace", letterSpacing: 4, marginTop: 4 }}>{league.code}</div>
            </div>
            <button onClick={copyCode} data-testid="copy-league-code" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: R.md, background: C.surface2, color: copied ? C.lime : C.text, fontSize: 12, fontWeight: 600 }}>
              <IoCopyOutline size={14} /> {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <div style={{ display: "flex", gap: SP.md, marginTop: SP.lg, paddingTop: SP.md, borderTop: `1px solid ${C.divider}` }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>DIVISIONS</div>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 18, marginTop: 4 }}>{league.divisions?.length || 0}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>POINTS</div>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 18, marginTop: 4 }}>{league.points_win}W / {league.points_loss}L</div>
            </div>
          </div>
          <button
            onClick={() => setShowShare(true)}
            data-testid="league-share-winner"
            disabled={!winner}
            style={{
              marginTop: SP.md, width: "100%", height: 44, borderRadius: R.md,
              background: winner ? C.lime : C.surface3,
              color: winner ? C.onLime : C.textMuted,
              fontWeight: 800, fontSize: 12, letterSpacing: 1, textTransform: "uppercase",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
              border: "none", cursor: winner ? "pointer" : "not-allowed",
            }}
          >
            <IoShareSocialOutline size={16} /> {winner ? `Share ${division ? `Div ${division} ` : ""}winner card` : "No winner yet"}
          </button>
        </Card>

        {canManage && (
          <div style={{ display: "flex", gap: SP.sm, marginTop: SP.md, flexWrap: "wrap" }}>
            <button
              onClick={() => setShowGen(true)}
              data-testid="open-fixture-generator"
              style={{ flex: 1, minWidth: 140, height: 44, borderRadius: R.md, background: C.lime, color: C.onLime, fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: "uppercase", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
            >
              <IoFlash size={14} /> Generate fixtures
            </button>
            <button
              onClick={() => navigate("/outstanding")}
              data-testid="open-outstanding"
              style={{ flex: 1, minWidth: 140, height: 44, borderRadius: R.md, background: C.surface, color: C.text, border: `1px solid ${C.borderStrong}`, fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: "uppercase", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
            >
              <IoCalendarOutline size={14} /> Outstanding
            </button>
            <button
              onClick={() => setShowManage(true)}
              data-testid="open-league-manage"
              style={{ flex: 1, minWidth: 140, height: 44, borderRadius: R.md, background: C.surface, color: C.text, border: `1px solid ${C.borderStrong}`, fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: "uppercase", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
            >
              <IoSettingsOutline size={14} /> Manage
            </button>
            <button
              onClick={() => { setToolsInitialTab("members"); setShowTools(true); }}
              data-testid="open-organiser-tools"
              style={{ flex: 1, minWidth: 140, height: 44, borderRadius: R.md, background: C.surface, color: C.lime, border: `1px solid ${C.lime}`, fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: "uppercase", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
            >
              <IoConstructOutline size={14} /> Tools
            </button>
          </div>
        )}
      </div>

      <div style={{ padding: `${SP.xl}px ${SP.lg}px ${SP.md}px` }}>
        <div style={{ display: "flex", gap: SP.sm, marginBottom: SP.md }}>
          <Pill label="Standings" active={tab === "standings"} onClick={() => setTab("standings")} testid="league-tab-standings" />
          <Pill label="Fixtures" active={tab === "fixtures"} onClick={() => setTab("fixtures")} testid="league-tab-fixtures" />
          <Pill label="Team" active={tab === "team"} onClick={() => setTab("team")} testid="league-tab-team" />
        </div>

        <div style={{ display: "flex", gap: SP.sm, overflowX: "auto", paddingBottom: SP.sm }}>
          {(league.divisions || []).map((d) => (
            <Pill key={d} label={`Div ${d}`} active={division === d} onClick={() => setDivision(d)} testid={`division-tab-${d}`} />
          ))}
        </div>
      </div>

      <div style={{ padding: `0 ${SP.lg}px` }}>
        {tab === "team" ? (
          <TeamPanel leagueId={lid} division={division} onChanged={loadLeague} />
        ) : tab === "standings" ? (
          <>
            <div style={{ display: "flex", gap: SP.sm, marginBottom: SP.md }} data-testid="standings-mode-toggle">
              <button
                type="button"
                onClick={() => setStandingsMode("players")}
                data-testid="standings-mode-players"
                style={{
                  flex: 1, height: 36, borderRadius: R.pill,
                  background: standingsMode === "players" ? C.lime : "transparent",
                  color: standingsMode === "players" ? C.onLime : C.text,
                  border: `1px solid ${standingsMode === "players" ? C.lime : C.borderStrong}`,
                  fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer",
                }}
              >Players</button>
              <button
                type="button"
                onClick={() => setStandingsMode("teams")}
                data-testid="standings-mode-teams"
                style={{
                  flex: 1, height: 36, borderRadius: R.pill,
                  background: standingsMode === "teams" ? C.lime : "transparent",
                  color: standingsMode === "teams" ? C.onLime : C.text,
                  border: `1px solid ${standingsMode === "teams" ? C.lime : C.borderStrong}`,
                  fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer",
                }}
              >Teams</button>
            </div>

            <div style={{ display: "flex", alignItems: "center", paddingBottom: SP.sm, borderBottom: `1px solid ${C.border}` }}>
              <div style={{ width: 28, color: C.textMuted, fontSize: 11, fontWeight: 700, textAlign: "center" }}>#</div>
              <div style={{ flex: 1, color: C.textMuted, fontSize: 11, fontWeight: 700 }}>{standingsMode === "players" ? "PLAYER" : "TEAM"}</div>
              <div style={{ width: 34, color: C.textMuted, fontSize: 11, fontWeight: 700, textAlign: "center" }}>P</div>
              <div style={{ width: 34, color: C.textMuted, fontSize: 11, fontWeight: 700, textAlign: "center" }}>W</div>
              <div style={{ width: 34, color: C.textMuted, fontSize: 11, fontWeight: 700, textAlign: "center" }}>L</div>
              <div style={{ width: 44, color: C.textMuted, fontSize: 11, fontWeight: 700, textAlign: "center" }}>PTS</div>
            </div>

            {standingsMode === "players" ? (
              (!standingsRows || standingsRows.length === 0) ? (
                <div style={{ color: C.textMuted, fontSize: 13, padding: `${SP.xl}px 0`, textAlign: "center" }} data-testid="league-empty-standings">No ranked players yet — play and confirm matches to populate this table.</div>
              ) : (
                <div data-testid="standings-table">
                  {standingsRows.map((r, i) => {
                    const rank = r.rank || i + 1;
                    const top = rank <= 3;
                    return (
                      <div key={r.user_id} style={{ display: "flex", alignItems: "center", padding: `${SP.md}px 0`, borderBottom: `1px solid ${C.divider}`, background: top ? C.surface2 : "transparent", borderRadius: top ? R.sm : 0 }}>
                        <div style={{ width: 28, color: top ? C.lime : C.text, fontWeight: 700, fontSize: 14, textAlign: "center" }}>{rank}</div>
                        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                          <Avatar uri={r.photo} name={r.name} size={30} />
                          <div style={{ color: C.text, fontSize: 14 }}>{r.name}</div>
                        </div>
                        <div style={{ width: 34, color: C.text, fontSize: 14, textAlign: "center" }}>{r.played ?? r.matches_played ?? 0}</div>
                        <div style={{ width: 34, color: C.text, fontSize: 14, textAlign: "center" }}>{r.wins ?? 0}</div>
                        <div style={{ width: 34, color: C.text, fontSize: 14, textAlign: "center" }}>{r.losses ?? 0}</div>
                        <div style={{ width: 44, color: C.lime, fontWeight: 700, fontSize: 14, textAlign: "center" }}>{r.points ?? 0}</div>
                      </div>
                    );
                  })}
                </div>
              )
            ) : (
              (!teamTable || teamTable.length === 0) ? (
                <div style={{ color: C.textMuted, fontSize: 13, padding: `${SP.xl}px 0`, textAlign: "center" }} data-testid="league-empty-team-standings">No teams yet — form teams in the Team tab to populate this table.</div>
              ) : (
                <div data-testid="team-standings-table">
                  {teamTable.map((t, i) => {
                    const rank = t.rank || i + 1;
                    const top = rank <= 3;
                    const playersLine = (t.players || []).map((p) => p.name.split(" ")[0]).join(" & ");
                    return (
                      <div key={t.team_id} data-testid={`team-standings-row-${t.team_id}`} style={{ display: "flex", alignItems: "center", padding: `${SP.md}px 0`, borderBottom: `1px solid ${C.divider}`, background: top ? C.surface2 : "transparent", borderRadius: top ? R.sm : 0 }}>
                        <div style={{ width: 28, color: top ? C.lime : C.text, fontWeight: 700, fontSize: 14, textAlign: "center" }}>{rank}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: C.text, fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</div>
                          {playersLine ? <div style={{ color: C.textMuted, fontSize: 11, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{playersLine}</div> : null}
                        </div>
                        <div style={{ width: 34, color: C.text, fontSize: 14, textAlign: "center" }}>{t.played ?? 0}</div>
                        <div style={{ width: 34, color: C.text, fontSize: 14, textAlign: "center" }}>{t.wins ?? 0}</div>
                        <div style={{ width: 34, color: C.text, fontSize: 14, textAlign: "center" }}>{t.losses ?? 0}</div>
                        <div style={{ width: 44, color: C.lime, fontWeight: 700, fontSize: 14, textAlign: "center" }}>{t.points ?? 0}</div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </>
        ) : (
          <div data-testid="fixtures-list" style={{ display: "flex", flexDirection: "column", gap: SP.sm }}>
            {canManage && filteredMatches.length > 0 && (
              <button
                onClick={() => { setToolsInitialTab("match"); setShowTools(true); }}
                data-testid="open-add-match"
                style={{ height: 40, borderRadius: R.md, background: "transparent", color: C.lime, border: `1px dashed ${C.lime}`, fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
              >
                + Add match manually
              </button>
            )}
            {filteredMatches.length === 0 ? (
              <div style={{ color: C.textMuted, fontSize: 13, padding: `${SP.xl}px 0`, textAlign: "center" }} data-testid="fixtures-empty">
                No fixtures yet{canManage ? ' — tap "Generate fixtures" above or add matches manually via Tools.' : "."}
              </div>
            ) : filteredMatches.map((m) => {
              const overdue = isOverdue(m);
              return (
                <Card
                  key={m.match_id}
                  onClick={() => navigate(`/matches/${m.match_id}`)}
                  testid={`fixture-row-${m.match_id}`}
                  style={{ border: `1px solid ${overdue ? C.warning : C.border}` }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SP.sm }}>
                    <span style={{ color: overdue ? C.warning : C.lime, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>{dateLabel(m.scheduled_at)}</span>
                    <FixtureStatusChip status={m.status} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ color: m.winner === "a" ? C.lime : C.text, fontWeight: 600, fontSize: 14, flex: 1 }}>{m.team_a_players?.map((p) => p.name.split(" ")[0]).join(" & ") || "TBC"}</span>
                    <span style={{ color: C.textDim, fontWeight: 700, fontSize: 12, margin: `0 ${SP.md}px` }}>VS</span>
                    <span style={{ color: m.winner === "b" ? C.lime : C.text, fontWeight: 600, fontSize: 14, flex: 1, textAlign: "right" }}>{m.team_b_players?.map((p) => p.name.split(" ")[0]).join(" & ") || "TBC"}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: SP.sm, paddingTop: SP.sm, borderTop: `1px solid ${C.divider}` }}>
                    <span style={{ color: C.textMuted, fontSize: 12 }}>Div {m.division || "—"}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: SP.sm }}>
                      {canManage && (
                        <button
                          type="button"
                          data-testid={`fixture-set-score-${m.match_id}`}
                          onClick={(e) => { e.stopPropagation(); setScoreMatch(m); }}
                          style={{ background: "transparent", border: `1px solid ${C.lime}`, color: C.lime, fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", padding: "4px 10px", borderRadius: R.pill, display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }}
                        >
                          <IoCreateOutline size={12} /> Set score
                        </button>
                      )}
                      <span style={{ color: overdue ? C.warning : C.lime, fontWeight: 700, fontSize: 11, letterSpacing: 1, display: "inline-flex", alignItems: "center", gap: 4 }}>
                        OPEN <IoArrowForward size={12} />
                      </span>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {showGen && <FixtureGeneratorModal league={league} onClose={() => setShowGen(false)} onGenerated={() => { setTab("fixtures"); loadMatches(); }} />}
      {showManage && <LeagueManageModal league={league} onClose={() => setShowManage(false)} onChanged={(opts) => { if (opts?.deleted) return; loadLeague(); }} />}
      {showTools && <OrganiserToolsModal league={league} initialTab={toolsInitialTab} onClose={() => setShowTools(false)} onChanged={() => { loadLeague(); loadMatches(); loadTable(); loadTeamTable(); }} />}
      {scoreMatch && <OrganiserScoreModal match={scoreMatch} onClose={() => setScoreMatch(null)} onSaved={() => { setScoreMatch(null); loadMatches(); loadTable(); loadTeamTable(); }} />}
      <ShareCardModal
        open={showShare}
        onClose={() => setShowShare(false)}
        variant="league"
        leagueName={league.name}
        division={division}
        winner={winner}
        runners={runners}
        dateLabel={league.season_end ? new Date(league.season_end).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "Vollea · LeaguePoint"}
        fileBaseName={`vollea-${(league.name || "league").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-div${division || "all"}`}
      />
    </div>
  );
}

function FixtureStatusChip({ status }) {
  const conf = {
    scheduled: { bg: C.surface3, fg: C.text, text: "Scheduled" },
    pending: { bg: "rgba(245,158,11,0.15)", fg: C.warning, text: "Pending" },
    confirmed: { bg: C.limeTint, fg: C.lime, text: "Confirmed" },
    disputed: { bg: "rgba(239,68,68,0.15)", fg: C.error, text: "Disputed" },
  }[status] || { bg: C.surface3, fg: C.textMuted, text: status };
  return (
    <span style={{ background: conf.bg, color: conf.fg, fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: R.pill, letterSpacing: 1, textTransform: "uppercase" }}>
      {conf.text}
    </span>
  );
}
